"use client";

import { useEffect, useState, useRef } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { AedSymbol } from "@/components/ui/AedSymbol";
import { GOLD_RATE_AED_PER_GRAM, SILVER_RATE_AED_PER_GRAM } from "@/lib/rates";
import type { LiveRates } from "@/lib/liveRates";
import { useLiveRatesContext } from "@/components/providers/LiveRatesProvider";

interface RateData {
  label: string;
  value: number;
  trend: "up" | "down" | "flat";
  change: number;
}

function trendOf(change: number): RateData["trend"] {
  return change > 0 ? "up" : change < 0 ? "down" : "flat";
}

function seedRates(liveRates?: LiveRates): RateData[] {
  const gold = liveRates?.goldBuy ?? GOLD_RATE_AED_PER_GRAM;
  const silver = liveRates?.silverBuy ?? SILVER_RATE_AED_PER_GRAM;
  const goldChange = liveRates?.goldChangePercent ?? 0.42;
  const silverChange = liveRates?.silverChangePercent ?? -0.08;

  return [
    { label: "Gold", value: gold, trend: trendOf(goldChange), change: goldChange },
    { label: "Silver", value: silver, trend: trendOf(silverChange), change: silverChange },
  ];
}

function AnimatedNumber({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  const shouldReduce = useReducedMotion();

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    if (shouldReduce) return;
    const start = displayRef.current;
    const diff = value - start;
    const steps = 12;
    let step = 0;
    const id = setInterval(() => {
      step++;
      const next = parseFloat((start + (diff * step) / steps).toFixed(2));
      displayRef.current = next;
      setDisplay(next);
      if (step >= steps) clearInterval(id);
    }, 20);
    return () => clearInterval(id);
  }, [shouldReduce, value]);

  return (
    <span>
      {(shouldReduce ? value : display).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  );
}

function PulseDot({ color }: { color: string }) {
  return (
    <span className="relative flex items-center justify-center w-2 h-2">
      <span
        className={`absolute inset-0 rounded-full ${color} animate-ping opacity-40`}
      />
      <span className={`relative w-1.5 h-1.5 rounded-full ${color}`} />
    </span>
  );
}

function RateItem({ rate }: { rate: RateData }) {
  return (
    <div className="flex items-center gap-2">
      {/* Pulse dot */}
      <PulseDot
        color={
          rate.trend === "up"
            ? "bg-success"
            : rate.trend === "down"
            ? "bg-danger"
            : "bg-neutral-400"
        }
      />

      {/* Label */}
      <span className="font-medium text-[11px] uppercase tracking-wide text-text-muted">
        {rate.label}
      </span>

      {/* Value */}
      <span className="font-semibold text-xs text-primary-900 tabular-nums">
        <AnimatedNumber value={rate.value} />
      </span>

      {/* Unit */}
      <span className="inline-flex items-center gap-0.5 text-text-muted text-[10px]">
        <AedSymbol className="h-[1em] w-[1em]" label="AED" />
        <span aria-hidden="true">/g</span>
        <span className="sr-only"> per gram</span>
      </span>

      {/* Trend icon */}
      <AnimatePresence mode="wait">
        <motion.span
          key={rate.trend}
          initial={{ y: rate.trend === "up" ? 4 : -4, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: rate.trend === "up" ? -4 : 4, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`flex items-center ${
            rate.trend === "up"
              ? "text-success"
              : rate.trend === "down"
              ? "text-danger"
              : "text-neutral-400"
          }`}
        >
          {rate.trend === "up" ? (
            <TrendingUp className="w-3 h-3" />
          ) : rate.trend === "down" ? (
            <TrendingDown className="w-3 h-3" />
          ) : null}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

export function GoldRateTicker() {
  const liveRates = useLiveRatesContext();
  const rates = seedRates(liveRates);

  return (
    <div
      className="flex items-center gap-3 px-4 py-2 rounded-[10px]
        bg-primary-50/50 border border-primary-100/80"
    >
      {rates.map((rate, i) => (
        <div key={rate.label} className="flex items-center gap-3">
          {i > 0 && (
            <div className="w-px h-4 bg-primary-200/60" aria-hidden="true" />
          )}
          <RateItem rate={rate} />
        </div>
      ))}
    </div>
  );
}
